"use client";

import { signIn } from "next-auth/react";
import { useState, useEffect, useRef, useCallback } from "react";
import Script from "next/script";
import { Loader2, Fingerprint } from "lucide-react";
import { Turnstile } from "@marsidev/react-turnstile";
import { clearAuthError, clearActivatedFlag } from "./actions";
import { notify } from "@/lib/notify";

// ── Google Identity Services (One Tap) — types tối thiểu, không có @types chính thức ──
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            use_fedcm_for_prompt?: boolean;
          }) => void;
          prompt: () => void;
          cancel: () => void;
        };
      };
    };
  }
}

// ── Google icon ───────────────────────────────────────────────────────────────
function GoogleG() {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
      <path d="M19.6 10.23c0-.68-.06-1.36-.18-2H10v3.79h5.4c-.23 1.23-.93 2.27-1.97 2.97v2.46h3.19c1.87-1.72 2.95-4.27 2.95-7.22z" fill="#4285F4" />
      <path d="M10 20c2.7 0 4.96-.9 6.62-2.42l-3.19-2.46c-.9.6-2.04.95-3.43.95-2.63 0-4.86-1.78-5.66-4.17H1.07v2.54C2.72 17.74 6.1 20 10 20z" fill="#34A853" />
      <path d="M4.34 11.9A5.98 5.98 0 0 1 4 10c0-.66.12-1.3.34-1.9V5.56H1.07A9.97 9.97 0 0 0 0 10c0 1.61.39 3.13 1.07 4.44l3.27-2.54z" fill="#FBBC05" />
      <path d="M10 3.98c1.48 0 2.8.51 3.84 1.5l2.87-2.87C14.95.99 12.7 0 10 0 6.1 0 2.72 2.26 1.07 5.56l3.27 2.54C5.14 5.76 7.37 3.98 10 3.98z" fill="#EA4335" />
    </svg>
  );
}

// ── Auth error text ───────────────────────────────────────────────────────────
const AUTH_ERROR_TEXT: Record<string, string> = {
  domain_mismatch: "Email này chưa được cấp quyền truy cập. Vui lòng liên hệ quản trị viên để được mời.",
  unauthorized: "Email này chưa được cấp quyền truy cập. Vui lòng liên hệ quản trị viên để được mời.",
  account_suspended: "Tài khoản bị khoá. Liên hệ quản trị viên.",
  account_terminated: "Tài khoản đã bị vô hiệu hoá. Liên hệ quản trị viên.",
  no_role: "Tài khoản chưa được cấp quyền truy cập. Liên hệ Admin.",
  server_error: "Đã xảy ra lỗi. Vui lòng thử lại.",
  session_revoked: "Phiên đăng nhập đã bị thu hồi từ thiết bị khác.",
  session_expired: "Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.",
  signed_out: "Đã đăng xuất thành công.",
};

type ErrorKey = keyof typeof AUTH_ERROR_TEXT;
type Step = "email" | "otp";

// ── Provider buttons (Google + Passkey) ──────────────────────────────────
function ProviderButtons({
  googleLoading,
  passkeyLoading,
  onGoogle,
  onPasskey,
}: {
  googleLoading: boolean;
  passkeyLoading: boolean;
  onGoogle: () => void;
  onPasskey: () => void;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {/* Google */}
      <button
        onClick={onGoogle}
        disabled={googleLoading || passkeyLoading}
        className="flex flex-row sm:flex-col items-center justify-center gap-2 sm:gap-1.5 h-[42px] sm:h-auto px-3 py-2.5 sm:py-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {googleLoading
          ? <Loader2 className="w-[18px] h-[18px] animate-spin text-slate-400" />
          : <GoogleG />}
        <span className="text-[13px] font-medium text-slate-700">Google</span>
      </button>

      {/* Passkey */}
      <button
        onClick={onPasskey}
        disabled={googleLoading || passkeyLoading}
        className="flex flex-row sm:flex-col items-center justify-center gap-2 sm:gap-1.5 h-[42px] sm:h-auto px-3 py-2.5 sm:py-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {passkeyLoading
          ? <Loader2 className="w-[18px] h-[18px] animate-spin text-slate-400" />
          : <Fingerprint className="w-[18px] h-[18px] text-slate-600" />}
        <span className="text-[13px] font-medium text-slate-700">Passkey</span>
      </button>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function LoginForm({
  errorKey,
  activated = false,
  orgName = "SonWorkSpace",
  tagline = { line1: "Welcome to SonWorkSpace", line2: "Đăng nhập vào tài khoản của bạn" },
  orgLogoIconUrl,
}: {
  errorKey: ErrorKey | null;
  activated?: boolean;
  orgName?: string;
  appName?: string;
  tagline?: { line1: string; line2: string };
  orgLogoWordmarkUrl?: string | null;
  orgLogoIconUrl?: string | null;
}) {
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [cfToken, setCfToken] = useState<string | null>(null);
  const [sendLoading, setSendLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(
    errorKey ? (AUTH_ERROR_TEXT[errorKey] ?? null) : null
  );
  const [countdown, setCountdown] = useState(0);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [isPopup] = useState(() => typeof window !== "undefined" && !!window.opener);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const sendingRef = useRef(false);
  const verifyingRef = useRef(false);
  const googleLoadingRef = useRef(false);
  const passkeyLoadingRef = useRef(false);
  const oneTapInitedRef = useRef(false);

  const siteKey = process.env.NEXT_PUBLIC_SWS_TURNSTILE_SITE_KEY;
  const isTestKey = typeof siteKey === "string" && siteKey.startsWith("1x00000000");

  // Clear auth error cookie on mount + relay error to opener popup if applicable
  useEffect(() => {
    if (!errorKey) return;
    clearAuthError();
    try {
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage({ type: "sws:oauth-error", errorKey }, window.location.origin);
        window.close();
      }
    } catch { }
  }, [errorKey]);

  // Banner "tài khoản đã được kích hoạt" — xoá cookie _activated sau khi đọc (dùng 1 lần)
  useEffect(() => {
    if (!activated) return;
    clearActivatedFlag();
  }, [activated]);

  // Google One Tap — ẩn widget nếu user đã chuyển sang bước nhập OTP
  useEffect(() => {
    if (step !== "email") window.google?.accounts.id.cancel();
  }, [step]);

  const handleOneTapCredential = useCallback(async (response: { credential: string }) => {
    try {
      const result = await signIn("google-onetap", { credential: response.credential, redirect: false });
      if (result?.ok && !result?.error) {
        window.location.replace("/");
        return;
      }
      // Thất bại (chưa được mời/sai domain/suspended...) — Credentials provider chỉ trả về
      // 1 lỗi chung "CredentialsSignin", không phân biệt được lý do cụ thể (khác nút Google
      // popup bên dưới, dùng OAuth provider nên redirect kèm mã lỗi riêng được) — hiện toast
      // chung chung, gợi ý dùng nút Google popup nếu cần biết lý do chính xác.
      if (result?.error) {
        notify.error("Không thể đăng nhập bằng tài khoản này", "Thử nút Google bên dưới để xem chi tiết");
      }
    } catch { }
  }, []);

  function handleGisLoad() {
    if (oneTapInitedRef.current || isPopup) return;
    const clientId = process.env.NEXT_PUBLIC_SWS_GOOGLE_CLIENT_ID;
    if (!clientId || !window.google) return;
    oneTapInitedRef.current = true;
    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: handleOneTapCredential,
      use_fedcm_for_prompt: true,
    });
    window.google.accounts.id.prompt();
  }

  // Resend countdown
  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  async function handleGoogleLogin() {
    if (googleLoadingRef.current) return;
    googleLoadingRef.current = true;
    setGoogleLoading(true);
    setFormError(null);

    try {
      const signInResult = await signIn("google", { redirect: false, callbackUrl: "/oauth-callback" });
      if (!signInResult?.url) {
        setFormError("Không thể kết nối với Google. Vui lòng thử lại.");
        googleLoadingRef.current = false; setGoogleLoading(false);
        return;
      }

      const w = 480, h = 580;
      const left = Math.round(window.screenX + (window.outerWidth - w) / 2);
      const top = Math.round(window.screenY + (window.outerHeight - h) / 2);
      const popup = window.open(
        signInResult.url,
        "google-oauth",
        `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no`
      );

      if (!popup) {
        window.location.href = signInResult.url;
        return;
      }

      let done = false;

      const cleanup = () => {
        done = true;
        clearInterval(closedCheck);
        window.removeEventListener("message", onMessage);
      };

      // Dùng chung cho cả nguồn postMessage (window.opener còn sống) lẫn
      // localStorage (fallback khi trình duyệt cắt window.opener sau khi
      // popup điều hướng qua accounts.google.com — Google set COOP nghiêm ngặt)
      const handleResult = (data: { type?: string; errorKey?: string } | undefined) => {
        if (data?.type === "sws:oauth-success") {
          cleanup();
          window.location.replace("/");
        } else if (data?.type === "sws:oauth-error") {
          cleanup();
          const key = data?.errorKey;
          if (key === "unauthorized" || key === "domain_mismatch") {
            setFormError("Email này chưa được cấp quyền truy cập. Vui lòng liên hệ quản trị viên để được mời.");
          } else if (key === "account_suspended") {
            setFormError("Tài khoản bị khoá. Liên hệ quản trị viên.");
          } else {
            setFormError("Đăng nhập Google thất bại. Vui lòng thử lại.");
          }
          googleLoadingRef.current = false; setGoogleLoading(false);
        }
      };

      const onMessage = (e: MessageEvent) => {
        if (e.origin !== window.location.origin) return;
        handleResult(e.data);
      };

      const readLocalStorageResult = (): boolean => {
        try {
          const raw = localStorage.getItem("sws-oauth-result");
          if (!raw) return false;
          localStorage.removeItem("sws-oauth-result");
          const data = JSON.parse(raw) as { type?: string; errorKey?: string; ts?: number };
          // Bỏ qua kết quả cũ (> 60s) — tránh đọc nhầm lần đăng nhập trước đó
          if (typeof data.ts === "number" && Date.now() - data.ts > 60_000) return false;
          handleResult(data);
          return true;
        } catch {
          return false;
        }
      };

      const closedCheck = setInterval(() => {
        if (!popup.closed || done) return;
        clearInterval(closedCheck);
        // Popup vừa đóng — kiểm tra localStorage NGAY (popup ghi trước khi close())
        if (readLocalStorageResult()) return;
        // Chưa có gì — chờ thêm 1 nhịp phòng khi postMessage/localStorage đang bay tới
        setTimeout(() => {
          if (done) return;
          if (readLocalStorageResult()) return;
          cleanup();
          setFormError("Đã huỷ đăng nhập Google.");
          googleLoadingRef.current = false; setGoogleLoading(false);
        }, 400);
      }, 500);

      window.addEventListener("message", onMessage);
      // storage event: nếu popup KHÔNG tự đóng được vì lý do nào đó nhưng vẫn
      // ghi được localStorage, tab chính vẫn nhận biết ngay mà không cần đợi
      // closedCheck phát hiện popup đóng.
      window.addEventListener("storage", (e) => {
        if (e.key === "sws-oauth-result" && e.newValue && !done) {
          readLocalStorageResult();
        }
      });

    } catch {
      setFormError("Không thể kết nối với Google. Vui lòng thử lại.");
      googleLoadingRef.current = false; setGoogleLoading(false);
    }
  }

  async function handlePasskeyLogin() {
    if (!window.PublicKeyCredential) {
      setFormError("Trình duyệt không hỗ trợ Passkey. Vui lòng dùng Chrome, Safari, hoặc Edge phiên bản mới nhất.");
      return;
    }
    if (passkeyLoadingRef.current) return;
    passkeyLoadingRef.current = true;
    setPasskeyLoading(true);
    setFormError(null);

    try {
      const startRes = await fetch("/api/auth/passkey/login/start", { method: "POST" });
      if (!startRes.ok) { setFormError("Không thể khởi động xác thực passkey."); return; }
      const { options, challengeId } = await startRes.json() as { options: unknown; challengeId: string };

      const { startAuthentication } = await import("@simplewebauthn/browser");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const assertion = await startAuthentication(options as any);

      const result = await signIn("passkey", {
        challengeId,
        assertionJSON: JSON.stringify(assertion),
        redirect: false,
      });
      if (!result?.ok || result?.error) {
        setFormError("Xác thực passkey thất bại. Vui lòng thử lại.");
      } else {
        window.location.replace("/");
      }
    } catch (err) {
      console.error("[Passkey login] client-side ceremony failed", err);
      if (err instanceof Error && err.name === "NotAllowedError") {
        setFormError("Đã huỷ xác thực Passkey.");
      } else {
        setFormError("Xác thực passkey thất bại. Vui lòng thử lại.");
      }
    } finally {
      passkeyLoadingRef.current = false;
      setPasskeyLoading(false);
    }
  }

  async function handleSendOtp() {
    // Ref đồng bộ — chặn gọi lồng (Enter lặp phím, double-click) trước khi setSendLoading
    // kịp re-render, vì state update là bất đồng bộ.
    if (sendingRef.current) return;
    sendingRef.current = true;

    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setFormError("Vui lòng nhập email.");
      sendingRef.current = false;
      return;
    }

    setSendLoading(true);
    setFormError(null);

    try {
      const res = await fetch("/api/auth/otp/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed, cfToken }),
      });
      const data = await res.json() as { success?: boolean; error?: string };

      if (!res.ok) {
        setFormError(data.error ?? "Không thể gửi mã. Vui lòng thử lại.");
        return;
      }

      setStep("otp");
      setCountdown(60);
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    } catch {
      setFormError("Lỗi kết nối. Vui lòng thử lại.");
    } finally {
      sendingRef.current = false;
      setSendLoading(false);
    }
  }

  const handleVerify = useCallback(async (code: string) => {
    if (code.length < 6) return;
    if (verifyingRef.current) return;
    verifyingRef.current = true;
    setVerifyLoading(true);
    setFormError(null);

    try {
      const result = await signIn("otp", { email, code, redirect: false });
      if (!result?.ok || result?.error) {
        setFormError("Mã OTP không đúng hoặc đã hết hạn.");
        setDigits(["", "", "", "", "", ""]);
        setTimeout(() => inputRefs.current[0]?.focus(), 50);
      } else {
        window.location.replace("/");
      }
    } catch {
      setFormError("Lỗi kết nối. Vui lòng thử lại.");
    } finally {
      verifyingRef.current = false;
      setVerifyLoading(false);
    }
  }, [email]);

  function handleDigitInput(i: number, val: string) {
    const d = val.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[i] = d;
    setDigits(next);
    if (d && i < 5) inputRefs.current[i + 1]?.focus();
    if (next.every(Boolean)) handleVerify(next.join(""));
  }

  function handleDigitKeyDown(i: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      inputRefs.current[i - 1]?.focus();
    }
  }

  function handlePaste(e: React.ClipboardEvent<HTMLDivElement>) {
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!text) return;
    e.preventDefault();
    const next = text.split("").concat(Array(6 - text.length).fill(""));
    setDigits(next);
    if (text.length === 6) handleVerify(text);
  }

  function goBackToEmail() {
    setStep("email");
    setDigits(["", "", "", "", "", ""]);
    setFormError(null);
    setCfToken(null);
  }

  // ── Render ───────────────────────────────────────────────────────────────
  // Nếu đang trong popup OAuth có lỗi → render trang trắng, effect sẽ tự đóng
  if (isPopup && errorKey) {
    return <div className="min-h-svh bg-white" />;
  }

  return (
    <div className="min-h-svh bg-white flex flex-col items-center justify-center p-4">
      {!isPopup && <Script src="https://accounts.google.com/gsi/client" strategy="afterInteractive" onLoad={handleGisLoad} />}
      <div className="w-full max-w-[420px] sm:max-w-[360px]">

        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img
            src={orgLogoIconUrl ?? "/ias_logo_sws.svg"}
            alt={orgName}
            className="w-8 h-8 object-contain block"
            draggable={false}
          />
        </div>

        {/* Tagline — chỉ hiện ở step email */}
        {step === "email" && (
          <h1 className="text-xl font-bold text-slate-900 text-center text-balance mb-6" style={{ lineHeight: 1.35 }}>
            <span className="block">{tagline.line1}</span>
            {tagline.line2 && <span className="block font-medium text-slate-400">{tagline.line2}</span>}
          </h1>
        )}

        {/* ── Step 1: Email ── */}
        {step === "email" && (
          <div>
            {/* Banner: tài khoản vừa được kích hoạt qua invite link */}
            {activated && (
              <p className="text-sm text-emerald-600 text-center mb-3">
                Tài khoản đã được kích hoạt, vui lòng đăng nhập.
              </p>
            )}

            {/* Email field */}
            <div className="space-y-1.5 mb-3">
              <label className="text-sm font-semibold text-slate-700 block">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleSendOtp(); }}
                placeholder="you@example.com"
                autoComplete="email"
                autoFocus
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-colors"
              />
              <p className="text-xs text-slate-400">Sử dụng email đã đăng ký để đăng nhập</p>
            </div>

            {/* Turnstile — interaction-only (invisible unless suspicious), bỏ qua ở dev (test key) */}
            {siteKey && !isTestKey ? (
              <div className="mb-3">
                <Turnstile
                  siteKey={siteKey}
                  onSuccess={setCfToken}
                  onExpire={() => setCfToken(null)}
                  onError={() => setCfToken(null)}
                  options={{ appearance: "interaction-only", theme: "light" }}
                />
              </div>
            ) : null}

            {/* Continue button */}
            <button
              onClick={handleSendOtp}
              disabled={sendLoading || googleLoading || passkeyLoading || !email.trim() || (!!siteKey && !isTestKey && !cfToken)}
              title={siteKey && !isTestKey && !cfToken ? "Đang xác minh bảo mật..." : undefined}
              className="
                w-full flex items-center justify-center gap-2
                h-[42px] rounded-md text-sm font-semibold text-white
                bg-blue-600 hover:bg-blue-700
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors
              "
            >
              {sendLoading
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Đang gửi...</>
                : "Tiếp tục"}
            </button>

            {/* Form error — hiện ở đây cho cả email lẫn provider errors */}
            {formError && (
              <p className="text-sm text-red-500 text-center mt-3">{formError}</p>
            )}

            {/* Divider + providers */}
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-sm text-slate-400">hoặc đăng nhập bằng</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            <ProviderButtons
              googleLoading={googleLoading}
              passkeyLoading={passkeyLoading}
              onGoogle={handleGoogleLogin}
              onPasskey={handlePasskeyLogin}
            />
          </div>
        )}

        {/* ── Step 2: OTP ── */}
        {step === "otp" && (
          <div>
            <h1 className="text-xl font-bold text-slate-900 text-center leading-tight mb-1.5">
              Kiểm tra email
            </h1>
            <p className="text-sm text-slate-500 text-center mb-6">
              Đã gửi mã đến <span className="font-medium text-slate-700">{email}</span>
            </p>

            {/* Form error */}
            {formError && (
              <p className="text-sm text-red-500 text-center mb-4">{formError}</p>
            )}

            {/* OTP boxes */}
            <div className="flex justify-center gap-2 mb-4" onPaste={handlePaste}>
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => { inputRefs.current[i] = el; }}
                  type="text"
                  inputMode="numeric"
                  maxLength={2}
                  value={d}
                  onChange={(e) => handleDigitInput(i, e.target.value)}
                  onKeyDown={(e) => handleDigitKeyDown(i, e)}
                  className="
                    w-11 h-[52px] rounded-md border border-slate-200 bg-white
                    text-center text-xl font-bold text-slate-900
                    focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400
                    transition-colors caret-transparent
                  "
                />
              ))}
            </div>

            {/* Verify button */}
            <button
              onClick={() => handleVerify(digits.join(""))}
              disabled={verifyLoading || digits.join("").length < 6}
              className="
                w-full flex items-center justify-center gap-2
                h-[42px] rounded-md text-sm font-semibold text-white
                bg-blue-600 hover:bg-blue-700
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors mb-4
              "
            >
              {verifyLoading
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Đang xác nhận...</>
                : "Xác nhận"}
            </button>

            {/* Resend + back */}
            <div className="flex flex-col items-center gap-2 mb-6">
              <p className="text-sm text-slate-500">
                Chưa nhận được?{" "}
                {countdown > 0 ? (
                  <span className="text-slate-400">
                    Gửi lại ({`0:${String(countdown).padStart(2, "0")}`})
                  </span>
                ) : (
                  <button
                    onClick={handleSendOtp}
                    disabled={sendLoading}
                    className="text-blue-600 hover:underline font-medium disabled:opacity-50"
                  >
                    Gửi lại
                  </button>
                )}
              </p>
              <button
                onClick={goBackToEmail}
                className="text-sm text-blue-600 hover:underline"
              >
                ← Đổi email
              </button>
            </div>

            {/* Divider + providers — luôn hiển thị ở step OTP */}
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-sm text-slate-400">hoặc tiếp tục bằng</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            <ProviderButtons
              googleLoading={googleLoading}
              passkeyLoading={passkeyLoading}
              onGoogle={handleGoogleLogin}
              onPasskey={handlePasskeyLogin}
            />
          </div>
        )}

      </div>

      {/* Footer */}
      <div className="fixed bottom-5 left-0 right-0 flex flex-col items-center gap-1 px-6">
        <p className="text-xs text-slate-400">
          Gặp sự cố?{" "}
          <a href="mailto:admin@sonworkspace.com" className="text-blue-500 hover:underline">
            Liên hệ
          </a>
        </p>
        <p className="text-xs text-slate-400 text-center">
          &copy; {new Date().getFullYear()} {orgName}. All rights reserved.
        </p>
      </div>
    </div>
  );
}
